import React, { FormEvent, useCallback, useState } from 'react';
import {
  Button,
  Combobox,
  ComboboxProps,
  makeStyles, mergeClasses,
  Option
} from '@fluentui/react-components';
import { DeleteRegular, SaveEditRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  dropdownWrapperStyle: {
    display: 'flex',
    flexDirection: 'row',
  },
  dropdownStyle: {
    minWidth: '100px',
    flex: 1,
  },
  saveButtonStyle: {
    flex: 0,
    marginLeft: '2px',
  },
  cancelButtonStyle: {
    flex: 0,
    marginLeft: '2px',
  },
});

export type DropdownOptions = { [value: string]: string };

export type DropdownValue = {
  value?: string;
  text: string;
};

export const DropdownAutocomplete = (props: {
  originalValue?: string;
  options: DropdownOptions;
  onSave: (newValue: DropdownValue) => void;
  onCancel: () => void;
  renderOption?: (option: DropdownOptions, index: number) => React.ReactNode;
  className?: string;
}) => {
  const { className, originalValue, options, onSave, onCancel, renderOption } = props;
  const styles = useStyles();
  const [value, setValue] = React.useState<DropdownValue | undefined>(
    originalValue
      ? options[originalValue]
        ? { value: originalValue, text: options[originalValue] }
        : undefined
      : undefined
  );
  const [matchingOptions, setMatchingOptions] = useState([
    ...Object.keys(options),
  ]);

  const doSave = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (value) {
        onSave(value);
      }
    },
    [onSave, value]
  );

  const doCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const onChange: ComboboxProps['onChange'] = (event) => {
    const value = event.target.value.trim();
    const matches = Object.keys(options).filter(
      (key) => options[key].toLowerCase().indexOf(value.toLowerCase()) === 0
    );
    setMatchingOptions(matches);
    if (value.length) {
      setValue({ value: undefined, text: value });
    } else {
      setValue(undefined);
    }
  };

  const onOptionSelect: ComboboxProps['onOptionSelect'] = (_, data) => {
    const matchingOption = data.optionValue
      ? options[data.optionValue]
      : undefined;
    if (matchingOption) {
      setValue({ value: data.optionValue, text: matchingOption });
    } else {
      setValue(undefined);
    }
  };

  return (
    <form className={mergeClasses(className, styles.dropdownWrapperStyle)} onSubmit={doSave}>
      <Combobox
        className={styles.dropdownStyle}
        freeform
        input={{
          style: {
            minWidth: '50px'
          }
        }}
        placeholder="Participant"
        defaultSelectedOptions={value?.value ? [value.value] : []}
        defaultValue={value?.text ? value.text : ''}
        onChange={onChange}
        onOptionSelect={onOptionSelect}
      >
        {matchingOptions.map((option, i) => (
          <Option value={option} key={option} text={options[option]}>
            {renderOption ? (
              renderOption(
                {
                  value: option,
                  text: options[option],
                },
                i
              )
            ) : (
              options[option]
            )}
          </Option>
        ))}
      </Combobox>
      <Button
        className={styles.saveButtonStyle}
        onClick={doSave}
        icon={<SaveEditRegular />}
        disabled={!value}
      />
      <Button
        className={styles.cancelButtonStyle}
        onClick={doCancel}
        icon={<DeleteRegular />}
      />
    </form>
  );
};
